import * as React from 'react';
import { useRouteLoaderData } from 'react-router';
import { ScrollArea as ScrollAreaPrimitive } from 'radix-ui';

import { cn } from '@april9au/react-common/utils';

import type { loader as rootLoader } from '@/root';

/**
 * The shadcn `scroll-area`, with the direction wired up.
 *
 * Radix's `Root` writes `dir="ltr"` onto its own element whenever it is given
 * no direction and finds no `DirectionProvider` above it — and that attribute
 * wins over the `dir` on `<html>` for everything inside. `DialogContent`
 * wraps its whole body in one of these, so in Arabic the dialog's copy, its
 * footer order and its close button were all still laid out left-to-right
 * inside a right-to-left document. Taking the direction from the root loader
 * — the same value that sets `dir` on the document — fixes it for every
 * consumer rather than one caller at a time.
 */
function ScrollArea({ className, children, dir, ...props }: React.ComponentProps<typeof ScrollAreaPrimitive.Root>) {
  // `useRouteLoaderData` rather than `useRootLoaderData`: this is a primitive
  // and it has to render outside a matched root route too.
  const root = useRouteLoaderData<typeof rootLoader>('root');

  return (
    <ScrollAreaPrimitive.Root
      data-slot="scroll-area"
      dir={dir ?? root?.direction}
      className={cn('relative', className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport data-slot="scroll-area-viewport" className="size-full rounded-[inherit]">
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
}

function ScrollBar({
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        'flex touch-none select-none',
        orientation === 'vertical' && 'h-full w-2.5 border-l border-l-transparent p-px',
        orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent p-px',
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className="bg-border relative flex-1 rounded-full"
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  );
}

export { ScrollArea, ScrollBar };
